/**
 * One person's face: their profile photo, or their initials on a tint that is
 * always the same for the same person. Decorative wherever a name sits beside
 * it; the stack's button carries the words.
 */
import { useState, type CSSProperties } from "react";

import { initialsOf } from "../../lib/names.js";
import { faceTint } from "./people.js";

export interface FaceProps {
  userId: string;
  name: string;
  avatarUrl?: string | null;
  size?: number;
  className?: string;
}

export function Face({ userId, name, avatarUrl, size = 32, className }: FaceProps) {
  const [failed, setFailed] = useState(false);
  const photo = avatarUrl && !failed ? avatarUrl : null;
  const tint = faceTint(userId);
  const style: CSSProperties = {
    width: size,
    height: size,
    fontSize: Math.max(9, Math.round(size * 0.38)),
    ...(photo ? {} : { background: tint.background, color: tint.color }),
  };
  return (
    <span
      className={`face${className ? ` ${className}` : ""}`}
      style={style}
      aria-hidden
      data-testid="person-face"
    >
      {photo ? (
        // Google photos 403 on a cross-origin Referer; `no-referrer` lets them load.
        <img src={photo} alt="" referrerPolicy="no-referrer" onError={() => setFailed(true)} />
      ) : (
        initialsOf(name)
      )}
    </span>
  );
}

/** Overlapping faces, at most `max`, then "+N". */
export function FaceStack({
  people,
  max,
  size = 32,
}: {
  people: ReadonlyArray<{ userId: string; name: string; avatarUrl?: string | null }>;
  max: number;
  size?: number;
}) {
  const shown = people.slice(0, max);
  const rest = people.length - shown.length;
  return (
    <span className="faces" style={{ "--face": `${size}px` } as CSSProperties}>
      {shown.map((person) => (
        <Face
          key={person.userId}
          userId={person.userId}
          name={person.name}
          avatarUrl={person.avatarUrl}
          size={size}
        />
      ))}
      {rest > 0 && (
        <span
          className="face more"
          style={{ width: size, height: size }}
          aria-hidden
          data-testid="person-face-more"
        >
          +{rest}
        </span>
      )}
    </span>
  );
}
